/**
 * Primitivas de validação reaproveitadas pelos schemas de domínio.
 *
 * Existem para que "mensagem com no mínimo 20 caracteres" (RN-11) seja **uma**
 * definição, e não uma por formulário: a observação da TELA-03, a mensagem da
 * TELA-05 e a justificativa de reabertura são o mesmo requisito de negócio, e o
 * banco tem um CHECK que só conhece um número.
 */
import { z } from 'zod'

import { PADRAO_DATA_ISO } from './datas'

/** RN-11 — mínimo de caracteres **úteis** (após `trim`) em texto de atendimento. */
export const TEXTO_ATENDIMENTO_MINIMO = 20
export const TEXTO_ATENDIMENTO_MAXIMO = 5000

/**
 * Identificador público em URL e em corpo (RNF-23). Nunca aceitar id sequencial:
 * `/leads/12345` permitiria enumerar a base inteira.
 */
export const zUuid = z.string().trim().uuid('Identificador inválido.')

/** PK de catálogo (`int`), que **é** público nos contratos: `{ "id": 2, ... }`. */
export const zIdCatalogo = z.coerce
  .number()
  .int('Identificador inválido.')
  .positive('Identificador inválido.')

/**
 * Texto de atendimento: observação de criação, mensagem de interação,
 * justificativa de reabertura. `trim` antes de contar, porque 20 espaços não são
 * 20 caracteres — e o CHECK do banco usa `char_length(btrim(mensagem))`.
 */
export const zTextoAtendimento = z
  .string({ required_error: 'Escreva a observação do atendimento.' })
  .trim()
  .min(
    TEXTO_ATENDIMENTO_MINIMO,
    `Insira ao menos ${TEXTO_ATENDIMENTO_MINIMO} caracteres.`,
  )
  .max(TEXTO_ATENDIMENTO_MAXIMO, 'Texto muito longo.')

/**
 * Motivo de transferência (RN-26: "motivo informado").
 *
 * Limite menor que o texto de atendimento, e de propósito: a transferência em
 * lote de 200 leads grava o mesmo motivo em todos, e exigir uma redação de 20
 * caracteres para "Realocação de carteira" empurraria o operador para o
 * preenchimento decorativo. 10 caracteres barram "ok" e "-" sem virar cerimônia.
 */
export const zMotivoTransferencia = z
  .string({ required_error: 'Informe o motivo da transferência.' })
  .trim()
  .min(10, 'Descreva o motivo da transferência com ao menos 10 caracteres.')
  .max(1000, 'Motivo muito longo.')

/** Instante em ISO-8601. A API recebe e devolve UTC (§0). */
export const zInstante = z
  .string()
  .datetime({ offset: true, message: 'Informe data e hora no formato ISO-8601.' })
  .transform((valor) => new Date(valor))

/** Data de filtro em `YYYY-MM-DD`, dia civil de São Paulo (RN-55). */
export const zDataFiltro = z
  .string()
  .trim()
  .regex(PADRAO_DATA_ISO, 'Use o formato AAAA-MM-DD.')

/**
 * Dinheiro como **string decimal** (§0: "nunca number — evita perda de precisão
 * em JS"). Convertido para `Decimal(14,2)` pelo repositório.
 */
export const zDinheiro = z
  .string()
  .trim()
  .regex(/^\d{1,12}(\.\d{1,2})?$/, 'Informe o valor no formato 350000.00.')

export const zBooleanoFiltro = z
  .enum(['true', 'false'])
  .transform((valor) => valor === 'true')

/** Busca textual livre (§6.5: trigram sobre nome + telefone + e-mail). */
export const zBusca = z.string().trim().min(1).max(120).optional()
